import React, { useState } from 'react';
import PropTypes from 'prop-types';
import './VideoAnalysis.css';
import { getDatabase, ref, set, onValue, update, get } from 'firebase/database';
import { getAuth } from 'firebase/auth';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend } from 'recharts';
import Badge from 'react-bootstrap/Badge';
import 'firebase/functions';
import axios from 'axios';

function VideoAnalysis({ isOpen3, onClose3, apiKey }) {
  const [stats, setStats] = useState({ avgViewsPerDay: 0, viewIncrease: 0, growthRate: 0 });
  const [videoUrl, setVideoUrl] = useState('');
  const [chartData, setChartData] = useState([]);
  const [showCharts, setShowCharts] = useState(false);
  const { currentUser } = getAuth();
  const uid = currentUser ? currentUser.uid : '';
  const [showInterestList, setShowInterestList] = useState(false);

  const handleCancelButtonClick = () => {
    onClose3();
  };

  const handleVideoUrlChange = (event) => {
    setVideoUrl(event.target.value);
  };
  
  const extractVideoId = (url) => {
    const regex = /(?:v=|youtu\.be\/)([\w-]+)/;
    const match = url.match(regex);
    return match ? match[1] : null;
  };
  
  const fetchVideoData = async (videoId, apiKey) => {
    const response = await fetch(
      `https://www.googleapis.com/youtube/v3/videos?id=${videoId}&part=snippet,statistics&key=${apiKey}`
    );
    const data = await response.json();
    return data.items[0];
  };
  
  const handleAnalyzeButtonClick = async () => {
    const videoId = extractVideoId(videoUrl);
    const videoData = await fetchVideoData(videoId, apiKey);
    const requestBody = {
      videoData: videoData,
      uid: uid,
      apiKey: apiKey,
    };
  
    try {
      const response = await axios.post("/analyze-video", requestBody, { withCredentials: true });
      if (response.data.error) {
        alert("잘못된 링크입니다");
      } else {
        console.log(response.data.message);
        alert("분석이 신청 되었습니다");
      }
    } catch (error) {
      console.error(error);
      alert("이미 분석이 신청된 영상입니다");
    }
  };
  
  

    const handleShowChartsClick = () => {
      loadChartData();
      setShowCharts(!showCharts);
    };
    const renderGrowthRateMessage = (growthRate) => {
      const growthRatePercentage = parseFloat(growthRate);
      const growthRateFactor = growthRatePercentage / 100;
    
      if (growthRatePercentage < 30) {
        return (
          <span>
            최근 조회수가 평소보다 {growthRateFactor.toFixed(1)}배 낮습니다.{' '}
            <Badge pill bg="dark">관심도 없음</Badge>
          </span>
        );
      } else if (growthRatePercentage >= 30 && growthRatePercentage < 80) {
        return (
          <span>
            최근 조회수가 평소보다 {growthRateFactor.toFixed(1)}배 낮습니다.{' '}
            <Badge pill bg="secondary">관심도 하락</Badge>
          </span>
        );
      } else if (growthRatePercentage >= 80 && growthRatePercentage < 120) {
        return (
          <span>
            최근 조회수가 평소보다 {growthRateFactor.toFixed(1)}배로 비슷합니다.{' '}
            <Badge pill bg="success">관심도 유지</Badge>
          </span>
        );
      } else if (growthRatePercentage >= 120 && growthRatePercentage < 200) {
        return (
          <span>
            최근 조회수가 평소보다 {growthRateFactor.toFixed(1)}배 높습니다.{' '}
            <Badge pill bg="warning">관심도 상승</Badge>
          </span>
        );
      } else {
        return (
          <span>
            최근 조회수가 평소보다 {growthRateFactor.toFixed(1)}배 높습니다.{' '}
            <Badge pill bg="danger">관심도 폭발</Badge>
          </span>
        );
      }
    };
    const loadChartData = async () => {
      const videoId = extractVideoId(videoUrl);
      const videoData = await fetchVideoData(videoId, apiKey);
    
      const db = getDatabase();
      const videoDataRef = ref(db, `videos/${videoId}/views`);
      const snapshot = await get(videoDataRef);
      const data = snapshot.val();
    
      if (data) {
        const chartData = [];
        Object.keys(data).forEach((timestamp) => {
          const viewGroup = data[timestamp];
          const groupData = [];
          viewGroup.forEach((views, index) => {
            const date = new Date(parseInt(timestamp));
            date.setDate(date.getDate() + index + 1);
            groupData.push({ name: date.toLocaleDateString(), views });
          });
          chartData.push({ timestamp, views: groupData });
        });
        setChartData(chartData);
    
        // 1. 전체 기간 일평균 조회수 계산
        const totalViews = parseInt(videoData.statistics.viewCount);
        const uploadDate = new Date(videoData.snippet.publishedAt);
        const currentDate = new Date();
        const daysSinceUpload = (currentDate - uploadDate) / (1000 * 60 * 60 * 24);
        const avgViewsPerDay = totalViews / daysSinceUpload;
    
        // 2. 3일간 일평균 조회수 계산
        const firstGroup = chartData[0].views;

        if (firstGroup.length >= 4) {
          const viewIncreaseSum = firstGroup.slice(1, 4).reduce((sum, viewData, index) => {
            return sum + (viewData.views - firstGroup[index].views);
          }, 0);
          const viewIncreaseAvg = viewIncreaseSum / 3;
    
          // 3. 전체 기간 대비 일평균 조회수 증가비율 계산
          const growthRate = (viewIncreaseAvg / avgViewsPerDay) * 100;
    
          // 통계 값을 상태에 저장
          setStats({
            avgViewsPerDay: avgViewsPerDay.toFixed(2),
            viewIncrease: viewIncreaseAvg.toFixed(2),
            growthRate: growthRate.toFixed(2),
          });
        } else {
          alert('분석된 데이터가 충분하지 않습니다. 데이터가 적어도 4일치 이상이 필요합니다.');
        }
      } else {
        alert('분석된 데이터가 없습니다. 먼저 분석 신청을 진행해주세요.');
      }
    };
    
    const renderCharts = () => {
      const lastGroupIndex = chartData.length - 1;
      const secondLastGroupIndex = chartData.length - 2;
    
      const leftChartData = secondLastGroupIndex >= 0 ? chartData[secondLastGroupIndex].views : [];
      const rightChartData = lastGroupIndex >= 0 ? chartData[lastGroupIndex].views : [];
    
      if (rightChartData.length === 4) {
        setChartData([
          ...chartData.slice(0, lastGroupIndex - 1),
          { name: chartData[lastGroupIndex].name, views: rightChartData },
          { name: new Date().toLocaleString(), views: [] },
        ]);
      }
      
      
      const getFourthDateInLeftChart = () => {
        if (leftChartData.length >= 4) {
          return leftChartData[3].name + " 의 데이터";
        } else {
          return "4일 뒤에 분석 결과가 출력됩니다";
        }
      };
      
      return (
        <div>
          <h5>최근 데이터</h5>
          <LineChart
            width={300}
            height={150}
            data={leftChartData}
            margin={{ top: 5, right: 20, bottom: 5, left: 0 }}
          >
            <XAxis dataKey="name" />
            <YAxis />
            <Tooltip />
            <CartesianGrid stroke="#f5f5f5" />
            <Line type="monotone" dataKey="views" stroke="#ff7300" yAxisId={0} />
          </LineChart>
          <h5>최신 데이터</h5>
          <LineChart
            width={300}
            height={150}
            data={rightChartData}
            margin={{ top: 5, right: 20, bottom: 5, left: 0 }}
          >
            <XAxis dataKey="name" />
            <YAxis />
            <Tooltip />
            <CartesianGrid stroke="#f5f5f5" />
            <Line type="monotone" dataKey="views" stroke="#ff7300" yAxisId={0} />
          </LineChart>
          <h3>{getFourthDateInLeftChart()}</h3>
          <table>
  <tbody>
    <tr>
      <td>전체기간 일평균 조회수</td>
      <td>{stats.avgViewsPerDay}</td>
    </tr>
    <tr>
      <td>3일간 일평균 조회수</td>
      <td>{stats.viewIncrease}</td>
    </tr>
    <tr>
      <td>전체기간 대비 일평균 조회수 증가비율</td>
      <td>{stats.growthRate}%</td>
    </tr>
    <tr>
      <td colspan="2">{renderGrowthRateMessage(stats.growthRate)}</td>
    </tr>
    <tr>
    <button onClick={() => setShowInterestList(!showInterestList)}>
  관심도란?
</button>
    </tr>

    {showInterestList && (
    <tr>
    <td colspan="2"><p>관심도란 최근데이터를 분석 했을때 해당 영상이 여전히<br/>
                       인기가 있는지, 없는지를 판별하는 척도 입니다 아래의<br/>
                       각 관심도 뱃지에 따라 총 5단계로 구분됩니다
                    </p>
                    <Badge pill bg="dark">관심도 없음</Badge>
                    <Badge pill bg="secondary">관심도 하락</Badge>
                    <Badge pill bg="success">관심도 유지</Badge>
                    <Badge pill bg="warning">관심도 상승</Badge>
                    <Badge pill bg="danger">관심도 폭발</Badge>
        
    </td>
    </tr>
    
    )}
  </tbody>
</table>
        </div>
        
      );
    };

    return (
      <div className={`modal ${isOpen3 ? 'open' : ''}`}>
        <div className="video-analysis">
          <input
            type="text"
            placeholder="영상 링크를 입력하세요"
            value={videoUrl}
            onChange={handleVideoUrlChange}
          />
          <button onClick={handleAnalyzeButtonClick}>분석 신청</button>
          <button onClick={handleShowChartsClick}>분석 현황</button>
          <button onClick={handleCancelButtonClick}>X</button>
          {showCharts && (
            <div className="chart">
              {renderCharts()}
            </div>
          )}
        </div>
      </div>
    );
  }

  VideoAnalysis.propTypes = {
    isOpen3: PropTypes.bool.isRequired,
    onClose3: PropTypes.func.isRequired,
    apiKey: PropTypes.string.isRequired,
  };

  export default VideoAnalysis;
